import { useMemo, useState } from "react";
import { Plus, Trash, Copy, WarningCircle, ArrowBendDownRight, Lightning, ArrowSquareOut, CloudArrowDown, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import NegativeFactors from "@/components/NegativeFactors";

const n = (v) => { const x = parseFloat(v); return isFinite(x) ? x : 0; };
const money = (x) => "₹" + Math.round(x).toLocaleString("en-IN");

// Indicative ± ranges for common valuation adjustments; a preset inserts the mid-point, user fine-tunes.
const ADJ_PRESETS = [
  { label: "Road width / frontage", lo: 2, hi: 10 },
  { label: "Corner / two-side open", lo: 3, hi: 8 },
  { label: "Shape & regularity", lo: -10, hi: -2 },
  { label: "Size / plottage", lo: -20, hi: -5 },
  { label: "Location / locality", lo: -10, hi: 15 },
  { label: "Development level", lo: -5, hi: 10 },
  { label: "Negotiation margin", lo: -8, hi: -2 },
];
const FACTOR_PRESETS = [
  { label: "Guideline revision lag", lo: 5, hi: 20 },
  { label: "Road frontage advantage", lo: 3, hi: 10 },
  { label: "Location premium", lo: 5, hi: 15 },
  { label: "Corner plot", lo: 3, hi: 8 },
  { label: "Development potential", lo: 5, hi: 12 },
  { label: "Irregular shape", lo: -10, hi: -3 },
];
const presetMid = (p) => Math.round((p.lo + p.hi) / 2);

function PresetChips({ presets, onPick, testid }) {
  return (
    <div className="flex flex-wrap gap-1 pt-1" data-testid={testid}>
      <span className="text-[10px] text-muted-foreground self-center mr-0.5 flex items-center gap-0.5"><Lightning size={10} /> Presets:</span>
      {presets.map((p) => (
        <button key={p.label} type="button" onClick={() => onPick(p)}
          title={`${p.label} — indicative ${p.lo > 0 ? "+" : ""}${p.lo}% to ${p.hi > 0 ? "+" : ""}${p.hi}% (inserts ${presetMid(p) > 0 ? "+" : ""}${presetMid(p)}%)`}
          className="text-[10px] px-2 py-0.5 rounded-full border bg-background hover:bg-accent hover:border-primary transition-colors"
          data-testid={`${testid}-${p.label.replace(/[^a-z]+/gi, "-").toLowerCase()}`}>
          {p.label} <span className="text-muted-foreground">{p.lo > 0 ? "+" : ""}{p.lo}…{p.hi > 0 ? "+" : ""}{p.hi}%</span>
        </button>
      ))}
    </div>
  );
}

export function computeRateModel(model) {
  const G = n(model.guideline_rate);
  const g = n(model.appreciation_g) / 100;
  const comps = model.comparables || [];
  const adjusted = comps.map((c) => {
    const C = n(c.rate), t = n(c.months);
    const at = Math.pow(1 + g, t / 12) - 1;
    const parts = [at, ...(c.adj || []).map((a) => n(a.pct) / 100)];
    const prod = parts.reduce((p, a) => p * (1 + a), 1);
    const cstar = C * prod;
    const ga = parts.reduce((s, a) => s + Math.abs(a), 0);
    return { C, at, cstar, ga, net: prod - 1, overNet: Math.abs(prod - 1) > 0.15, overGa: ga > 0.25 };
  });
  const zero = adjusted.filter((a) => a.ga === 0);
  const invSum = adjusted.reduce((s, a) => s + (a.ga > 0 ? 1 / a.ga : 0), 0);
  adjusted.forEach((a) => {
    a.weight = zero.length ? (a.ga === 0 ? 1 / zero.length : 0)
      : (a.ga > 0 && invSum > 0 ? (1 / a.ga) / invSum : (adjusted.length ? 1 / adjusted.length : 0));
  });
  const M = adjusted.reduce((s, a) => s + a.weight * a.cstar, 0);
  const mean = adjusted.length ? adjusted.reduce((s, a) => s + a.cstar, 0) / adjusted.length : 0;
  const varc = adjusted.length ? adjusted.reduce((s, a) => s + Math.pow(a.cstar - mean, 2), 0) / adjusted.length : 0;
  const cov = mean ? Math.sqrt(varc) / mean : 0;
  const R = G ? M / G : 0;
  const lnR = R > 0 ? Math.log(R) : 0;
  const Fs = (model.factors || []).map((f) => ({ label: f.label || "Factor", F: 1 + n(f.pct) / 100 }));
  const prodF = Fs.reduce((p, f) => p * f.F, 1);
  const Fres = prodF > 0 && R > 0 ? R / prodF : 1;
  const apportion = Fs.map((f) => {
    const lnF = Math.log(f.F);
    const share = lnR !== 0 ? lnF / lnR : 0;
    return { label: f.label, F: f.F, share, rupees: share * (M - G) };
  });
  const residual = { F: Fres, share: lnR !== 0 ? Math.log(Fres) / lnR : 0, rupees: (lnR !== 0 ? Math.log(Fres) / lnR : 0) * (M - G) };
  const resOk = Math.abs(Fres - 1) <= 0.05;
  return { G, M, mean, cov, R, adjusted, apportion, residual, resOk, covWarn: cov > 0.15 };
}

export function rateModelParagraph(model) {
  const r = computeRateModel(model);
  if (!r.G || !r.M) return "";
  const nComp = (model.comparables || []).length;
  const shares = r.apportion.map((a) => `${a.label} ${(a.share * 100).toFixed(1)}%`).join(", ");
  return `The fair market rate has been derived from ${nComp} comparable transaction(s), each adjusted multiplicatively for time, road width, location and physical attributes (Annexure). The comparables were reconciled using weights inversely proportional to their gross adjustments, giving ${money(r.M)}/sq.ft (CoV ${(r.cov * 100).toFixed(1)}%). Against the Collector Guideline rate of ${money(r.G)}/sq.ft, the ratio is R = ${r.R.toFixed(3)}. Log-proportional apportionment attributes the variance to: ${shares} and residual ${(r.residual.share * 100).toFixed(1)}%. The residual is ${r.resOk ? "within" : "outside"} the 5% tolerance${r.resOk ? ", so the variance is fully explained by the identified attributes." : " — the comparables / factors should be reviewed."}`;
}

export default function RateModelTab({ value, onChange, onAdopt, source, onPatchVd, adopted }) {
  const model = value || {};
  const comparables = model.comparables || [];
  const factors = model.factors || [];
  const patch = (p) => onChange({ ...model, ...p });
  const r = useMemo(() => computeRateModel(model), [model]);

  const src = source || {};
  const [gl, setGl] = useState({ rate: src.rate || "", unit: src.unit || "sqft", district: src.district || "", tehsil: src.tehsil || "", village: src.village || "" });
  const [glBusy, setGlBusy] = useState(false);
  const tryFetchGuideline = async () => {
    setGlBusy(true);
    try {
      const { data } = await api.post("/geo/guideline-rate", { district: gl.district, tehsil: gl.tehsil, village: gl.village });
      if (data.ok && data.rate) { setGl((g) => ({ ...g, rate: String(data.rate), unit: data.unit || g.unit })); toast.success("Fetched guideline rate from portal"); }
      else { window.open(data.portal_url, "_blank"); toast.message(data.message || "Read the rate from the portal and enter it here"); }
    } catch { toast.error("Couldn't auto-fetch — open the portal and enter the rate manually"); }
    finally { setGlBusy(false); }
  };
  const applyGuideline = () => {
    const raw = parseFloat(gl.rate);
    if (!isFinite(raw) || raw <= 0) { toast.error("Enter a valid guideline rate first"); return; }
    const perSqft = gl.unit === "sqm" ? raw / 10.7639 : raw;
    const rounded = Math.round(perSqft);
    patch({ guideline_rate: rounded });
    const today = new Date().toISOString().slice(0, 10);
    const loc = [gl.district, gl.tehsil, gl.village].filter(Boolean).join(" / ");
    const remark = `Collector guideline ₹${rounded}/sq.ft${gl.unit === "sqm" ? ` (from ₹${raw}/sq.m)` : ""}${loc ? ` — ${loc}` : ""} (IGRS, ${today})`;
    if (onPatchVd) onPatchVd({
      guideline_rate_per_sqft: rounded,
      guideline_source: { ...gl, rate_per_sqft: rounded, source_url: "https://igrs.cgstate.gov.in/en/market-price-rate", captured_date: today },
      doc_checklist: { g1: { status: "Collected", remarks: remark } },
    });
    toast.success(`Guideline rate ₹${rounded}/sq.ft applied to G, the report Guideline rate & checklist item G1`);
  };

  const setComp = (i, upd) => patch({ comparables: comparables.map((c, k) => (k === i ? { ...c, ...upd } : c)) });
  const addComp = () => patch({ comparables: [...comparables, { rate: "", months: "", adj: [] }] });
  const removeComp = (i) => patch({ comparables: comparables.filter((_, k) => k !== i) });
  const addAdj = (i) => setComp(i, { adj: [...(comparables[i].adj || []), { label: "", pct: "" }] });
  const setAdj = (i, j, upd) => setComp(i, { adj: (comparables[i].adj || []).map((a, k) => (k === j ? { ...a, ...upd } : a)) });
  const removeAdj = (i, j) => setComp(i, { adj: (comparables[i].adj || []).filter((_, k) => k !== j) });
  const setFactor = (i, upd) => patch({ factors: factors.map((f, k) => (k === i ? { ...f, ...upd } : f)) });
  const addFactor = () => patch({ factors: [...factors, { label: "", pct: "" }] });
  const removeFactor = (i) => patch({ factors: factors.filter((_, k) => k !== i) });

  const pct = (x) => (x * 100).toFixed(1) + "%";
  const para = rateModelParagraph(model);

  const cc = model.crosscheck || {};
  const ccArea = n(cc.area);
  const ccDisc = n(cc.discount) / 100;
  const setCC = (upd) => patch({ crosscheck: { ...cc, ...upd } });
  const ccRate = r.M * (1 - ccDisc);
  const ccSmallVal = ccArea * r.M;
  const ccLargeVal = ccArea * ccRate;
  const ccDivergence = ccSmallVal ? (ccSmallVal - ccLargeVal) / ccSmallVal : 0;

  return (
    <div className="space-y-5" data-testid="rate-model-tab">
      <p className="text-xs text-muted-foreground">Guideline-based market-rate model: comparables are adjusted multiplicatively, reconciled by inverse-gross-adjustment weights, and the gap to the guideline rate is apportioned across factors using a log-proportional split.</p>

      <label className="flex items-center gap-2 text-xs font-medium rounded-lg border bg-muted/30 p-2.5 cursor-pointer" data-testid="rm-include-row">
        <Checkbox checked={model.include_in_report !== false} onCheckedChange={(v) => patch({ include_in_report: v === true })} data-testid="rm-include-toggle" />
        Include the <b>Adjustment-Model annexure</b> (Market Rate vs Guideline Rate) in the PDF report
      </label>

      {/* Guideline Rate Assistant */}
      <div className="rounded-lg border p-3 space-y-2 bg-background" data-testid="guideline-assistant">
        <Label className="text-sm font-semibold flex items-center gap-1.5"><ArrowSquareOut size={14} /> Guideline Rate Assistant (CG IGRS / Panjiyan)</Label>
        <p className="text-[11px] text-muted-foreground">Open the IGRS Market-Price-Rate portal, read the notified Collector rate for the location, capture it here — it auto-fills the guideline rate (G) below and marks checklist item G1 as collected.</p>
        <div className="grid sm:grid-cols-3 gap-2">
          <Input placeholder="District" value={gl.district} onChange={(e) => setGl({ ...gl, district: e.target.value })} className="h-8 text-xs" data-testid="gl-district" />
          <Input placeholder="Tehsil" value={gl.tehsil} onChange={(e) => setGl({ ...gl, tehsil: e.target.value })} className="h-8 text-xs" data-testid="gl-tehsil" />
          <Input placeholder="Village / locality" value={gl.village} onChange={(e) => setGl({ ...gl, village: e.target.value })} className="h-8 text-xs" data-testid="gl-village" />
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1"><Label className="text-[11px]">Notified rate</Label><Input type="number" value={gl.rate} onChange={(e) => setGl({ ...gl, rate: e.target.value })} placeholder="e.g. 16000" className="h-8 w-32 text-xs" data-testid="gl-rate" /></div>
          <select value={gl.unit} onChange={(e) => setGl({ ...gl, unit: e.target.value })} className="h-8 rounded-md border border-border bg-background px-2 text-xs" data-testid="gl-unit"><option value="sqft">₹ / sq.ft</option><option value="sqm">₹ / sq.m</option></select>
          <Button type="button" size="sm" variant="outline" className="h-8 rounded-full text-xs" onClick={() => window.open("https://igrs.cgstate.gov.in/en/market-price-rate", "_blank")} data-testid="gl-open"><ArrowSquareOut size={13} className="mr-1" /> Open IGRS portal</Button>
          <Button type="button" size="sm" variant="outline" className="h-8 rounded-full text-xs" disabled={glBusy} onClick={tryFetchGuideline} data-testid="gl-fetch"><CloudArrowDown size={13} className="mr-1" /> {glBusy ? "Trying…" : "Try auto-fetch"}</Button>
          <Button type="button" size="sm" className="h-8 rounded-full text-xs bg-primary text-white hover:bg-[#113A64]" onClick={applyGuideline} data-testid="gl-apply"><CheckCircle size={13} className="mr-1" /> Apply to G &amp; G1</Button>
        </div>
        {src.rate_per_sqft ? <p className="text-[11px] text-green-700 flex items-center gap-1"><CheckCircle size={12} /> Captured: ₹{src.rate_per_sqft}/sq.ft{src.captured_date ? ` on ${src.captured_date}` : ""}</p> : null}
      </div>


      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Collector Guideline rate G (₹/sq.ft)</Label>
          <Input type="number" value={model.guideline_rate ?? ""} onChange={(e) => { patch({ guideline_rate: e.target.value }); if (onPatchVd) onPatchVd({ guideline_rate_per_sqft: e.target.value }); }} placeholder="e.g. 1500" className="h-9" data-testid="rm-guideline" />
          <p className="text-[10px] text-accent">Also fills the report's “Guideline rate (Registrar's Office)” &amp; Guideline Value.</p>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Local appreciation g (% per year, for time adj.)</Label>
          <Input type="number" value={model.appreciation_g ?? ""} onChange={(e) => patch({ appreciation_g: e.target.value })} placeholder="e.g. 8" className="h-9" data-testid="rm-appreciation" />
        </div>
      </div>

      {/* Comparables */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold">Step 1–2 · Comparables &amp; reconciliation</Label>
          <Button type="button" size="sm" variant="outline" className="rounded-full text-xs" onClick={addComp} data-testid="rm-add-comparable"><Plus size={13} className="mr-1" /> Add comparable</Button>
        </div>
        {comparables.length === 0 && <p className="text-xs text-muted-foreground">No comparables yet.</p>}
        {comparables.map((c, i) => {
          const a = r.adjusted[i] || {};
          return (
            <div key={i} className="rounded-lg border p-3 space-y-2 bg-background" data-testid={`rm-comp-${i}`}>
              <div className="flex flex-wrap items-end gap-2">
                <div className="space-y-1"><Label className="text-[11px]">Rate C (₹/sq.ft)</Label><Input type="number" value={c.rate} onChange={(e) => setComp(i, { rate: e.target.value })} className="h-8 w-28 text-xs" data-testid={`rm-comp-rate-${i}`} /></div>
                <div className="space-y-1"><Label className="text-[11px]">Months since sale</Label><Input type="number" value={c.months} onChange={(e) => setComp(i, { months: e.target.value })} className="h-8 w-28 text-xs" data-testid={`rm-comp-months-${i}`} /></div>
                <div className="text-[11px] text-muted-foreground pb-1">Time adj: <b>{a.at != null ? pct(a.at) : "—"}</b></div>
                <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-destructive ml-auto" onClick={() => removeComp(i)} data-testid={`rm-comp-del-${i}`}><Trash size={14} /></Button>
              </div>
              <div className="pl-1 space-y-1">
                {(c.adj || []).map((ad, j) => (
                  <div key={j} className="flex items-center gap-2">
                    <Input value={ad.label} onChange={(e) => setAdj(i, j, { label: e.target.value })} placeholder="Factor (e.g. Road width)" className="h-7 text-xs flex-1" data-testid={`rm-adj-label-${i}-${j}`} />
                    <Input type="number" value={ad.pct} onChange={(e) => setAdj(i, j, { pct: e.target.value })} placeholder="±%" className="h-7 text-xs w-20" data-testid={`rm-adj-pct-${i}-${j}`} />
                    <Button type="button" size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => removeAdj(i, j)}><Trash size={12} /></Button>
                  </div>
                ))}
                <Button type="button" size="sm" variant="ghost" className="text-[11px] h-6 px-2" onClick={() => addAdj(i)} data-testid={`rm-add-adj-${i}`}><Plus size={11} className="mr-1" /> Add adjustment</Button>
                <PresetChips presets={ADJ_PRESETS} testid={`rm-adj-presets-${i}`}
                  onPick={(p) => setComp(i, { adj: [...(comparables[i].adj || []), { label: p.label, pct: String(presetMid(p)) }] })} />
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] border-t pt-1.5">
                <span>Adjusted C* = <b className="text-primary">{money(a.cstar || 0)}</b></span>
                <span>Net = <b className={a.overNet ? "text-destructive" : ""}>{a.net != null ? pct(a.net) : "—"}</b></span>
                <span>Gross GA = <b className={a.overGa ? "text-destructive" : ""}>{a.ga != null ? pct(a.ga) : "—"}</b></span>
                <span>Weight = <b>{a.weight != null ? pct(a.weight) : "—"}</b></span>
                {(a.overNet || a.overGa) && <span className="text-destructive flex items-center gap-1"><WarningCircle size={12} /> exceeds ±15%/25% limit</span>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Factors */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold">Step 3 · Variance factors (Fᵢ = 1 + rᵢ)</Label>
          <Button type="button" size="sm" variant="outline" className="rounded-full text-xs" onClick={addFactor} data-testid="rm-add-factor"><Plus size={13} className="mr-1" /> Add factor</Button>
        </div>
        {factors.map((f, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`rm-factor-${i}`}>
            <Input value={f.label} onChange={(e) => setFactor(i, { label: e.target.value })} placeholder="e.g. Guideline revision lag / Road frontage / Location" className="h-8 text-xs flex-1" data-testid={`rm-factor-label-${i}`} />
            <Input type="number" value={f.pct} onChange={(e) => setFactor(i, { pct: e.target.value })} placeholder="±%" className="h-8 text-xs w-20" data-testid={`rm-factor-pct-${i}`} />
            <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => removeFactor(i)}><Trash size={13} /></Button>
          </div>
        ))}
        <PresetChips presets={FACTOR_PRESETS} testid="rm-factor-presets"
          onPick={(p) => patch({ factors: [...factors, { label: p.label, pct: String(presetMid(p)) }] })} />
      </div>

      {/* Results */}
      <div className="rounded-lg border bg-muted/30 p-3 space-y-2" data-testid="rm-results">
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
          <span>Adopted market rate M = <b className="text-primary">{money(r.M)}/sq.ft</b></span>
          <span>Ratio R = M/G = <b>{r.R.toFixed(3)}</b> ({r.G ? ((r.R - 1) * 100).toFixed(1) : "0"}% vs guideline)</span>
          <span>CoV = <b className={r.covWarn ? "text-destructive" : ""}>{pct(r.cov)}</b>{r.covWarn && <span className="text-destructive"> (&gt;15% — review)</span>}</span>
          {n(adopted) > 0 && <span data-testid="rm-adopted-parta">Part A adopted rate = <b className="text-accent">{money(n(adopted))}/sq.ft</b></span>}
          {r.M > 0 && onAdopt && (
            <Button type="button" size="sm" variant="outline" className="rounded-full text-xs h-7 ml-auto" onClick={() => { onAdopt(r.M); toast.success(`Adopted ${money(r.M)}/sq.ft into Part A`); }} data-testid="rm-adopt-m">
              <ArrowBendDownRight size={13} className="mr-1" /> Use M as Part A rate
            </Button>
          )}
        </div>
        {r.apportion.length > 0 && (
          <table className="w-full text-xs mt-1">
            <thead><tr className="text-muted-foreground text-left"><th className="py-1">Factor</th><th>Fᵢ</th><th>Share of variance</th><th className="text-right">₹/sq.ft</th></tr></thead>
            <tbody>
              {r.apportion.map((a, i) => (
                <tr key={i} className="border-t"><td className="py-1">{a.label}</td><td>{a.F.toFixed(3)}</td><td>{pct(a.share)}</td><td className="text-right">{money(a.rupees)}</td></tr>
              ))}
              <tr className="border-t"><td className="py-1">Residual (unexplained)</td><td className={r.resOk ? "" : "text-destructive"}>{r.residual.F.toFixed(3)}</td><td>{pct(r.residual.share)}</td><td className="text-right">{money(r.residual.rupees)}</td></tr>
              <tr className="border-t font-semibold"><td className="py-1">Total</td><td /><td /><td className="text-right">{money(r.M - r.G)}</td></tr>
            </tbody>
          </table>
        )}
        {!r.resOk && r.G > 0 && <p className="text-[11px] text-destructive flex items-center gap-1"><WarningCircle size={12} /> Residual |F−1| exceeds 5% — the variance is not fully justified by the listed factors.</p>}
        {r.R > 0 && r.R < 0.9 && <p className="text-[11px] text-amber-600">Note: market rate is &gt;10% below guideline — outside the Sec. 50C / 56(2)(x) safe harbour; cross-check with the residual method.</p>}
      </div>

      {/* Large-extent cross-check */}
      <div className="rounded-lg border p-3 space-y-2 bg-background" data-testid="rm-crosscheck">
        <Label className="text-sm font-semibold">Large-extent cross-check</Label>
        <p className="text-[11px] text-muted-foreground">Comparables are usually small/medium plots. For a large-extent plot, apply a size/plottage discount to the adopted rate M and reconcile the lump-sum value.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-[11px]">Subject plot area (sq.ft)</Label><Input type="number" value={cc.area ?? ""} onChange={(e) => setCC({ area: e.target.value })} placeholder="e.g. 43560" className="h-8 text-xs" data-testid="rm-cc-area" /></div>
          <div className="space-y-1"><Label className="text-[11px]">Size / plottage discount (%)</Label><Input type="number" value={cc.discount ?? ""} onChange={(e) => setCC({ discount: e.target.value })} placeholder="e.g. 15" className="h-8 text-xs" data-testid="rm-cc-discount" /></div>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs border-t pt-2">
          <span>Large-extent rate = <b className="text-primary" data-testid="rm-cc-rate">{money(ccRate)}/sq.ft</b></span>
          <span>At M (no discount) = <b>{money(ccSmallVal)}</b></span>
          <span>Large-extent value = <b className="text-primary" data-testid="rm-cc-value">{money(ccLargeVal)}</b></span>
          {ccArea > 0 && ccDisc > 0 && <span className="text-muted-foreground">Reduction = {money(ccSmallVal - ccLargeVal)} ({pct(ccDivergence)})</span>}
          {ccDisc > 0.3 && <span className="text-amber-600 flex items-center gap-1"><WarningCircle size={12} /> discount &gt;30% is unusually high — justify</span>}
        </div>
        {ccRate > 0 && onAdopt && (
          <Button type="button" size="sm" variant="outline" className="rounded-full text-xs h-7" onClick={() => { onAdopt(ccRate); toast.success(`Adopted large-extent rate ${money(ccRate)}/sq.ft into Part A`); }} data-testid="rm-adopt-cc">
            <ArrowBendDownRight size={13} className="mr-1" /> Use large-extent rate as Part A rate
          </Button>
        )}
      </div>

      <NegativeFactors G={r.G} baseRate={r.M} value={model.negative || {}} onChange={(neg) => patch({ negative: neg })} onAdopt={onAdopt} />

      {/* Paragraph */}
      {para && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-semibold">Report paragraph</Label>
            <Button type="button" size="sm" variant="outline" className="rounded-full text-xs" onClick={() => { try { navigator.clipboard.writeText(para).then(() => toast.success("Copied")).catch(() => toast.error("Copy failed — select and copy manually")); } catch { toast.error("Copy failed — select and copy manually"); } }} data-testid="rm-copy-paragraph"><Copy size={13} className="mr-1" /> Copy</Button>
          </div>
          <p className="text-xs leading-relaxed bg-background border rounded-md p-3" data-testid="rm-paragraph">{para}</p>
        </div>
      )}
    </div>
  );
}
